import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Autocomplete,
  AutocompleteInput,
  AutocompleteItem,
} from '@/components/ui/Autocomplete'
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
} from '@/components/ui/Combobox'
import type { ControlSize } from '@/components/ui/control'
import { useParticipants } from '@/features/directories/api/useDirectory'
import type { DirectoryParticipant } from '@/types/domain'

/* Who the buy side is with, chosen from the counterparty directory (FR-29,
 * FR-40, migration 0044).
 *
 * ── Why it suggests instead of constraining ─────────────────────────────────
 *
 * This is a text field with a list attached, not a picker. Migration 0019 wrote
 * the reason down when it made the column free text: at quoting time the agent
 * "is frequently a name from an email thread and not yet a counterparty record,
 * and forcing a directory entry to save a draft line would make the salesperson
 * invent one". 0044 adds the LINK without touching that — the id is nullable
 * precisely so the email-thread agent still saves — and this control is the
 * same bargain on screen: pick a counterparty and the line remembers which one,
 * type a name and the line keeps the name.
 *
 * There is a second reason a strict list would be wrong, and it is FR-29's.
 * The participants directory hides AGENT rows from Sales at the ROW level — a
 * Sales caller does not receive them at all. A control that only accepted its
 * own options would therefore have taken from Sales the ability to record an
 * agent as a vendor, which is a thing they can do today by typing.
 *
 * ── How the link is decided ─────────────────────────────────────────────────
 *
 * From the TEXT, by exact match, and never from a separate click handler. Base
 * UI's autocomplete fills the field with the pressed row's label, so pressing a
 * row and typing that same name by hand arrive here identically — which is the
 * property worth having: a name typed exactly links, and no ordering question
 * exists between "the item was pressed" and "the value changed".
 *
 * A name matching TWO directory rows links to neither. That is the honest
 * answer — the control cannot know which was meant — and the line still saves,
 * carrying the name alone. Rare enough to accept, common enough to say out loud.
 */

export interface VendorPickerProps {
  /** The vendor's name. This is the value the line stores and the offer prints. */
  value: string
  /** The directory row the name resolves to, or null. Derived, never typed. */
  onChange: (name: string, vendorId: string | null) => void
  disabled?: boolean
  placeholder?: string
  /** Drops the control box for a cell inside the costing table. */
  bare?: boolean
  size?: ControlSize
  id?: string
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  className?: string
}

export function VendorPicker({
  value,
  onChange,
  disabled,
  placeholder,
  bare,
  size = 'md',
  id,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: VendorPickerProps) {
  const { t } = useTranslation()

  /* Every type, unfiltered: a forwarder pays agents and counter-agents most
     often, but also vehicle owners and dispatchers, and a shipper occasionally
     bills for something. Narrowing the list here would be this component
     deciding who the company is allowed to owe money to.

     One unparameterised read, so the query is shared with every other surface
     asking for the roster rather than cached per search term. The filtering is
     the control's own, over a list this size — the directory is a company's
     counterparties, not a public register. */
  const { data: roster } = useParticipants()

  /* Active rows only. A retired counterparty stays on the lines that already
     name it — 0044's ON DELETE SET NULL and the stored name both see to that —
     but offering it for a NEW line is offering somebody the company has stopped
     working with. */
  const options = useMemo(
    () => (roster ?? []).filter((person) => person.isActive),
    [roster],
  )

  /* Name → the one row that owns it, lower-cased so a difference in
     capitalisation still links. Built as a map of name to a SINGLE id, with
     duplicates resolved to null rather than to the first match: see the header.
     `undefined` and `null` are different here — absent means no row of that
     name, null means too many. */
  const byName = useMemo(() => {
    const seen = new Map<string, string | null>()
    for (const person of options) {
      const key = person.name.trim().toLowerCase()
      seen.set(key, seen.has(key) ? null : person.id)
    }
    return seen
  }, [options])

  const linkFor = (name: string): string | null =>
    byName.get(name.trim().toLowerCase()) ?? null

  const roleLabel = (role: DirectoryParticipant['type']) =>
    t(`participantRole.${role}` as 'participantRole.agent', { defaultValue: role })

  return (
    <Autocomplete
      items={options}
      value={value}
      onValueChange={(next: string) => onChange(next, linkFor(next))}
      /* What a pressed row puts in the field. The NAME, because the name is
         what the line stores, what freezes into the revision (AD-15) and what
         the offer prints — the id is only ever the route back to the row. */
      itemToStringValue={(person: DirectoryParticipant) => person.name}
      /* The list opens on focus rather than on the first keystroke. A reader who
         does not know what is in the directory cannot search it, and this field
         is the first place many of them meet the counterparty list at all. */
      openOnInputClick
      /* NOT modal, and this is load-bearing rather than a preference. A modal
         popup marks the rest of the page inert while it is open — which, on a
         field that lives in a row of a table the user is filling in, takes the
         quantity and price cells beside it out of reach for as long as the
         suggestions are showing. Select.tsx refuses modality for the neighbouring
         reason (it locks page scroll inside a Sheet); here the cost is the row
         itself. It is also what the tests see: an inert table has no accessible
         Add button, so the row could not be found at all. */
      modal={false}
    >
      <AutocompleteInput
        id={id}
        inputSize={size}
        bare={bare}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        className={className}
      />

      <ComboboxContent>
        {/* Not a failure. Nothing matching is the ordinary case for the agent
            off an email thread, and the hint says the typed name is kept rather
            than leaving the reader to guess whether the field took it. */}
        <ComboboxEmpty hint={t('quotes.costLines.vendorFreeTextHint')}>
          {t('quotes.costLines.vendorNoMatches')}
        </ComboboxEmpty>

        <ComboboxList>
          {(person: DirectoryParticipant) => (
            <AutocompleteItem key={person.id} value={person}>
              <span className="min-w-0 flex-1 truncate">{person.name}</span>
              {/* The type as the second reading of the same row: two
                  counterparties can share a trading name across roles, and a
                  list of bare names cannot be chosen from. */}
              <span className="text-text-muted shrink-0 text-[12px] font-light">
                {roleLabel(person.type)}
              </span>
            </AutocompleteItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Autocomplete>
  )
}
